import { useCallback } from "react";
import { useFetch2 } from "../../util";

export type CannedMessageItem = {
  id: number;
  shortcut: string;
  title: string;
  message: string;
  finalizeDialog: boolean;
};

export type GetAllCannedMessageInput = {
  search?: string;
  offset?: number;
  limit?: number;
};

export type GetAllCannedMessageResponse = {
  result: CannedMessageItem[];
  total: number;
};

export type CreateCannedMessageInput = {
  shortcut: string;
  title: string;
  message: string;
  finalizeDialog?: boolean;
};

export type CreateCannedMessageResponse = {
  id: number;
};

export type UpdateCannedMessageInput = {
  id: number;
  shortcut: string;
  title: string;
  message: string;
  finalizeDialog?: boolean;
};

export type UpdateCannedMessageResponse = {
  id: number;
};

export type DeleteCannedMessageResponse = {
  id: number;
};

export default function useCannedMessage() {
  const fetch = useFetch2();

  const getAllCannedMessages = useCallback((input?: GetAllCannedMessageInput) => {
    const query = new URLSearchParams();

    if (input?.search && input.search.trim()) {
      query.set("search", input.search);
    }

    query.set("offset", `${input?.offset ?? 0}`);
    query.set("limit", `${input?.limit ?? 10}`);

    const url = query.toString()
      ? `/api/canned-messages?${query.toString()}`
      : "/api/canned-messages";

    return fetch<GetAllCannedMessageResponse>(url, { method: "GET" });
  }, [fetch]);

  const createCannedMessage = useCallback((input: CreateCannedMessageInput) => {
    return fetch<CreateCannedMessageResponse>(
      "/api/canned-messages/create",
      { method: "POST" },
      input
    );
  }, [fetch]);

  const updateCannedMessage = useCallback((input: UpdateCannedMessageInput) => {
    return fetch<UpdateCannedMessageResponse>(
      "/api/canned-messages",
      { method: "PATCH" },
      input
    );
  }, [fetch]);

  const deleteCannedMessage = useCallback((id: number) => {
    return fetch<DeleteCannedMessageResponse>(
      `/api/canned-messages/${id}`,
      { method: "DELETE" }
    );
  }, [fetch]);

  return {
    getAllCannedMessages,
    createCannedMessage,
    updateCannedMessage,
    deleteCannedMessage
  };
}
